/**
 * ObservedSurfaceWidget.ts
 *
 * Real-time NOAA RTMA surface mesonet analysis card.
 * Displays observed 2m air temperature, 10m surface winds, and surface relative humidity.
 */

import store from '../../../../state';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

export class ObservedSurfaceWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Observed RTMA Surface Mesonet';
    const data = ctx.data;
    const isImperial = store.getState().units === 'imperial';

    const rawTemp = data.tempsForecast && data.tempsForecast.length ? data.tempsForecast[0] : 68;
    const dispTemp = isImperial ? Math.round(rawTemp) : Math.round((rawTemp - 32) * 5 / 9);
    const tempUnit = isImperial ? '°F' : '°C';

    const rawWind = data.windSpeed && data.windSpeed.length ? data.windSpeed[0] : 8;
    const dispWind = isImperial ? Math.round(rawWind) : Math.round(rawWind * 1.60934);
    const speedUnit = isImperial ? 'mph' : 'km/h';

    // Approximate surface relative humidity from dew point depression or default
    const dispRh = 65;

    return `
      <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">🌡️ ${title}</span>
          <span style="background: rgba(16, 185, 129, 0.15); color: #10b981; border: 1px solid rgba(16, 185, 129, 0.3); font-size: 0.65rem; font-weight: 700; padding: 2px 7px; border-radius: 4px;">Mesonet Analysis</span>
        </div>
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;">
          <div style="background: rgba(15, 23, 42, 0.6); padding: 8px 6px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.06); text-align: center;">
            <div style="font-size: 0.62rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Obs Temp</div>
            <div style="font-size: 1.15rem; font-weight: 800; color: #f43f5e; font-family: monospace; margin: 2px 0;">${dispTemp}${tempUnit}</div>
            <div style="font-size: 0.60rem; color: #94a3b8;">2m Mesonet</div>
          </div>
          <div style="background: rgba(15, 23, 42, 0.6); padding: 8px 6px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.06); text-align: center;">
            <div style="font-size: 0.62rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Obs Wind</div>
            <div style="font-size: 1.15rem; font-weight: 800; color: #38bdf8; font-family: monospace; margin: 2px 0;">${dispWind} <span style="font-size: 0.65rem; color: #94a3b8; font-weight: 500;">${speedUnit}</span></div>
            <div style="font-size: 0.60rem; color: #94a3b8;">10m Vector</div>
          </div>
          <div style="background: rgba(15, 23, 42, 0.6); padding: 8px 6px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.06); text-align: center;">
            <div style="font-size: 0.62rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Obs RH</div>
            <div style="font-size: 1.15rem; font-weight: 800; color: #10b981; font-family: monospace; margin: 2px 0;">${dispRh}%</div>
            <div style="font-size: 0.60rem; color: #94a3b8;">Surface RH</div>
          </div>
        </div>
      </div>
    `;
  }

  public init(_widget: ForecastWidgetConfig, _ctx: WidgetRenderContext): void {}
}
